import { VerityAssessmentType } from "@verity/database/prisma";
import { useContext, useMemo } from "react";
import { PortalControllerContext } from "../../../../context";
import {
	validateAssessmentDetailsStep,
	validateAddInstructionStep,
	validateQuestionsStep,
	getAssessmentDurationErrorMessage,
	getDueDateErrorMessage,
	getLateSubmissionErrorMessage,
	validateAssessmentDuration,
	validateDueDate,
	validateLateSubmissionDate,
	isHourError,
	isMinuteError,
	isEndDateError,
	isStartDateError,
	QuizDraftQuestion,
} from "../verityAssessmentHelper";
import { AddInstructionsReturn } from "./useAddInstructions";
import { AssessmentDetailsReturn } from "./useAssessmentDetails";
import { Course } from "./useFetchVerityAssessmentInfo";
import { AssessmentDuration } from "../AssessmentDetailsBlock";
import { Module } from "@verity/api-client/types";

export function useTimingBlockValidation({
	startDate,
	endDate,
	allowLateSubmissions,
	lateSubmissionDate,
	assessmentDuration,
}: {
	startDate: Date | null;
	endDate: Date | null;
	allowLateSubmissions: boolean;
	lateSubmissionDate: Date | null;
	assessmentDuration: AssessmentDuration | null;
}) {
	const dueDateErrors = useMemo(() => {
		return validateDueDate(startDate, endDate);
	}, [startDate, endDate]);

	const lateSubmissionDateErrors = useMemo(() => {
		if (!allowLateSubmissions) return undefined;
		return validateLateSubmissionDate(endDate, lateSubmissionDate);
	}, [allowLateSubmissions, endDate, lateSubmissionDate]);

	const durationErrors = useMemo(() => {
		return validateAssessmentDuration(assessmentDuration);
	}, [assessmentDuration]);

	const startError = useMemo(() => {
		return dueDateErrors?.find((error) => isStartDateError(error));
	}, [dueDateErrors]);

	const endError = useMemo(() => {
		return dueDateErrors?.find((error) => isEndDateError(error));
	}, [dueDateErrors]);

	const startErrorMessage = useMemo(() => {
		return getDueDateErrorMessage(startError);
	}, [startError]);

	const endErrorMessage = useMemo(() => {
		return getDueDateErrorMessage(endError);
	}, [endError]);

	const lateSubmissionDateErrorMessage = useMemo(() => {
		return getLateSubmissionErrorMessage(lateSubmissionDateErrors?.[0]);
	}, [lateSubmissionDateErrors]);

	const hourError = useMemo(() => {
		return durationErrors?.find((error) => isHourError(error));
	}, [durationErrors]);

	const minuteError = useMemo(() => {
		return durationErrors?.find((error) => isMinuteError(error));
	}, [durationErrors]);

	const hourErrorMessage = useMemo(() => {
		return getAssessmentDurationErrorMessage(hourError);
	}, [hourError]);

	const minuteErrorMessage = useMemo(() => {
		return getAssessmentDurationErrorMessage(minuteError);
	}, [minuteError]);

	return {
		startErrorMessage,
		endErrorMessage,
		lateSubmissionDateErrorMessage,
		hourErrorMessage,
		minuteErrorMessage,
	};
}

type UseValidateVerityAssessmentPageProps = {
	assessmentType: VerityAssessmentType;
	assessmentDetails: AssessmentDetailsReturn;
	addInstructions: AddInstructionsReturn;
	courses: Course[];
	modules: Module[];
	questions: QuizDraftQuestion[];
};

export default function useValidateVerityAssessmentPage({
	assessmentDetails,
	addInstructions,
	assessmentType,
	courses,
	modules,
	questions,
}: UseValidateVerityAssessmentPageProps) {
	const { portalContext } = useContext(PortalControllerContext);
	const { lms } = portalContext;
	const assessmentDetailsErrors = useMemo(
		() => validateAssessmentDetailsStep(assessmentDetails, courses, modules, lms),
		[assessmentDetails, courses, modules, lms],
	);

	const addInstructionErrors = useMemo(() => {
		if (assessmentType !== "assignment") return [];
		return validateAddInstructionStep(addInstructions.uploadedFiles);
	}, [assessmentType, addInstructions.uploadedFiles]);

	const questionsErrors = useMemo(() => {
		if (assessmentType !== "quiz") return [];
		return validateQuestionsStep(questions);
	}, [assessmentType, questions]);

	return {
		assessmentDetailsErrors,
		addInstructionErrors,
		questionsErrors,
	};
}
